import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

export type DevicePlatform = "android" | "ios" | "ipados" | "windows" | "macos" | "linux" | "other";

export function detectPlatform(): DevicePlatform {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent;
  const isIPad =
    /iPad/.test(ua) ||
    (navigator.platform === "MacIntel" && (navigator as any).maxTouchPoints > 1);
  if (isIPad) return "ipados";
  if (/iPhone|iPod/.test(ua)) return "ios";
  if (/Android/.test(ua)) return "android";
  if (/Windows/.test(ua)) return "windows";
  if (/Macintosh|Mac OS X/.test(ua)) return "macos";
  if (/Linux/.test(ua)) return "linux";
  return "other";
}

export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (window.navigator as any).standalone === true
  );
}

const DISMISS_KEY = "kelasai_pwa_dismissed_at";
const DISMISS_DAYS = 7;

export function usePWAInstall() {
  const [platform] = useState<DevicePlatform>(() => detectPlatform());
  const [installed, setInstalled] = useState<boolean>(() => isStandalone());
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      void logInstallation("appinstalled");
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const isIOSLike = platform === "ios" || platform === "ipados";
  const canPrompt = !!deferredPrompt;
  const canInstall = !installed && (canPrompt || isIOSLike);

  const recentlyDismissed = useCallback(() => {
    try {
      const v = localStorage.getItem(DISMISS_KEY);
      if (!v) return false;
      return Date.now() - Number(v) < DISMISS_DAYS * 24 * 3600 * 1000;
    } catch {
      return false;
    }
  }, []);

  const markDismissed = useCallback(() => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
  }, []);

  const promptInstall = useCallback(async (source: string = "prompt") => {
    if (!deferredPrompt) return { ok: false, outcome: "unsupported" as const };
    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    if (choice.outcome === "accepted") {
      setInstalled(true);
      await logInstallation(source);
    } else {
      markDismissed();
    }
    return { ok: choice.outcome === "accepted", outcome: choice.outcome };
  }, [deferredPrompt, markDismissed]);

  return {
    platform,
    installed,
    canInstall,
    canPrompt,
    isIOSLike,
    promptInstall,
    markDismissed,
    recentlyDismissed,
  };
}

export async function logInstallation(source: string) {
  try {
    const { data } = await supabase.auth.getUser();
    if (!data.user) return;
    const platform = detectPlatform();
    await (supabase as any).from("pwa_installations").insert({
      user_id: data.user.id,
      platform,
      device_type: /Mobi|Android|iPhone|iPad/.test(navigator.userAgent) ? "mobile" : "desktop",
      user_agent: navigator.userAgent.slice(0, 500),
      source,
    });
  } catch (e) {
    console.warn("PWA install log failed", e);
  }
}